import React, { useEffect, useRef } from 'react';
import { RightOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import './index.css';

const industries = [
  {
    name: '服装制造\n服装制造业',
    nameEn: 'Garment Manufacturing',
    nameVi: 'Sản xuất hàng dệt',
    description:
      '从内衣到西装，从童装到职业装，小麦铺布机以稳定的面料输送和精确的张力控制，确保每一层布料平整无皱，大幅提升裁剪精度与缝制效率。',
    descriptionEn:
      'From underwear to suits, children\'s wear to professional garments, Xiaomai spreaders ensure each layer of fabric is smooth and wrinkle-free, significantly improving cutting accuracy and sewing efficiency.',
    descriptionVi:
      'Từ đồ lót đến bộ vest, từ đồ trẻ em đến trang phục chuyên nghiệp, máy trải vải Xiaomai đảm bảo mỗi lớp vải mềm mại và không có nếp nhăn, nâng cao đáng kể độ chính xác cắt may và hiệu quả gia công.',
    imageUrl: '/assets/industries/industry1.png',
    align: 'left',
  },
  {
    name: '家纺家居\n家居家纺行业',
    nameEn: 'Home Textiles',
    nameVi: 'Vải trang trí nhà',
    description:
      '床单、被套、窗帘、沙发布——大尺寸面料铺设对均匀性和平整度要求极高。小麦设备在高负荷连续作业下依然保持卓越的布料张力控制，保障大宽幅面料的完美铺设。',
    descriptionEn:
      'Bedding, curtains, sofa fabrics — large-size fabric spreading demands exceptional uniformity and flatness. Xiaomai equipment maintains superior tension control under high-load continuous operation.',
    descriptionVi:
      'Ga giường, vỏ gối, rèm cửa, vải bọc ghế sofa — việc trải vải khổ lớn đòi hỏi độ đồng đều và phẳng cực cao. Thiết bị Xiaomai duy trì kiểm soát lực căng vải vượt trội ngay cả khi vận hành liên tục với tải nặng.',
    imageUrl: '/assets/industries/industry2.png',
    align: 'right',
  },
  {
    name: '汽车内饰\n汽车内饰行业',
    nameEn: 'Automotive Interiors',
    nameVi: 'Nội thất ô tô',
    description:
      '汽车座椅面料、顶棚材料，地毯基层——这类材料往往更厚、更硬，传统设备难以处理。小麦铺布机凭借强劲驱动与精密张力调节，轻松应对各类复合材料。',
    descriptionEn:
      'Automotive seat fabrics, headliner materials, carpet substrates — these thicker, harder materials challenge conventional equipment. Xiaomai\'s powerful drive and precise tension adjustment handle composite materials with ease.',
    descriptionVi:
      'Vải bọc ghế ô tô, vật liệu trần xe, đế thảm — những vật liệu này thường dày và cứng hơn, khiến thiết bị truyền thống khó xử lý. Máy trải vải Xiaomai dễ dàng xử lý các loại vật liệu composite nhờ công suất mạnh và điều chỉnh lực căng chính xác.',
    imageUrl: '/assets/industries/industry3.png',
    align: 'left',
  },
  {
    name: '箱包皮具\n箱包皮具行业',
    nameEn: 'Leather Goods & Luggage',
    nameVi: 'Hàng da & Hành lý',
    description:
      '真皮、合成革、帆布——不同材质对面料张力与推进速度的要求差异显著。小麦设备可灵活调节参数，一机多用，有效降低采购与切换成本。',
    descriptionEn:
      'Genuine leather, synthetic leather, canvas — different materials demand distinct tension and speed settings. Xiaomai\'s flexible parameter adjustment enables multi-purpose use on a single machine.',
    descriptionVi:
      'Da thật, da giả, vải bạt — các vật liệu khác nhau đòi hỏi lực căng và tốc độ tiến khác nhau. Thiết bị Xiaomai có thể linh hoạt điều chỉnh thông số, sử dụng đa năng trên một máy.',
    imageUrl: '/assets/industries/industry4.png',
    align: 'right',
  },
  {
    name: '复合材料\n复合材料应用',
    nameEn: 'Composite Materials',
    nameVi: 'Vật liệu composite',
    description:
      '碳纤维预浸料、玻璃纤维、芳纶布——高性能复合材料在铺布时对张力均匀性和无皱要求极为严苛。小麦子母机专为这类精密材料设计，提供精确到克的张力控制。',
    descriptionEn:
      'Carbon fiber prepreg, fiberglass, aramid fabric — high-performance composites demand extremely precise tension control and wrinkle-free spreading. The Xiaomai Mother-Child machine is specifically designed for such precision materials.',
    descriptionVi:
      'Vật liệu composite hiệu suất cao như sợi carbon, sợi thủy tinh, vải aramid đòi hỏi kiểm soát lực căng đồng đều và không nếp nhăn cực kỳ nghiêm ngặt. Máy Mẹ-Con Xiaomai được thiết kế đặc biệt cho các vật liệu chính xác này.',
    imageUrl: '/assets/industries/industry5.png',
    align: 'left',
  },
];

function IndustrySection() {
  const { t, i18n } = useTranslation();
  const listRef = useRef(null);

  const getIndustryName = (item) => {
    const lang = i18n.language;
    if (lang.startsWith('en')) return item.nameEn;
    if (lang.startsWith('vi')) return item.nameVi;
    return item.name;
  };

  const getIndustryDescription = (item) => {
    const lang = i18n.language;
    if (lang.startsWith('en')) return item.descriptionEn;
    if (lang.startsWith('vi')) return item.descriptionVi;
    return item.description;
  };

  const isZh = !i18n.language.startsWith('en') && !i18n.language.startsWith('vi');
  const isVi = i18n.language.startsWith('vi');

  useEffect(() => {
    const items = listRef.current?.querySelectorAll('.industry-item');
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="industry-section">
      {/* Header */}
      <div className="industry-section-header">
        <div className="industry-section-eyebrow">
          {isZh ? '应用领域' : isVi ? 'Lĩnh vực ứng dụng' : 'Applications'}
        </div>
        <h2 className="industry-section-title">{t('apply_industry')}</h2>
        <div className="industry-section-rule" />
        <Tooltip title={t('view_more')}>
          <RightOutlined className="industry-section-more" />
        </Tooltip>
      </div>

      {/* Items */}
      <div className="industry-list" ref={listRef}>
        {industries.map((item, index) => (
          <div
            key={index}
            className={`industry-item industry-item--${item.align} industry-item--${index + 1}`}
          >
            {/* Image */}
            <div className="industry-item__media">
              <div className="industry-item__media-inner">
                <img
                  src={item.imageUrl}
                  alt={getIndustryName(item).replace('\n', '')}
                  className="industry-item__img"
                  loading="lazy"
                />
                {/* Serial badge */}
                <div className="industry-item__badge">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="industry-item__body">
              <div className="industry-item__category">
                {isZh ? '应用行业' : isVi ? 'Ngành' : 'Industry'}
              </div>
              <h3 className="industry-item__name">
                {isZh || i18n.language.startsWith('zh')
                  ? item.name.split('\n').map((line, i) => (
                      <span key={i}>
                        {i === 0 ? (
                          <span className="industry-item__name-line1">{line}</span>
                        ) : (
                          <span className="industry-item__name-line2"> {line}</span>
                        )}
                        {i === 0 && <br />}
                      </span>
                    ))
                  : getIndustryName(item)}
              </h3>
              <div className="industry-item__divider" />
              <p className="industry-item__desc">
                {getIndustryDescription(item)}
              </p>
              <div className="industry-item__cta">
                <span>{isZh ? '了解更多' : isVi ? 'Tìm hiểu thêm' : 'Learn more'}</span>
                <RightOutlined className="industry-item__cta-arrow" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default IndustrySection;
